import { Button, Typography } from '@mui/material';
import type { PublicApiKeyItem } from '@provision/entity-types';
import { DynamicIcon } from 'src/components';
import { useDeletePublicApiKey, usePublicApiKeys, useRotatePublicApiKey } from 'src/graphql';
import { Modals, useShowModals } from 'src/store/modals';
import { useConfirmationModal } from 'src/utils/hooks';
import { ApiKeysEmptyState } from './api-keys-empty-state';
import { ApiKeysTable } from './api-keys-table';
import {
  DeveloperBody,
  DeveloperDocsLink,
  DeveloperHeader,
  DeveloperHeaderText,
  DeveloperTabRoot,
} from './styled';

const PUBLIC_API_DOCS_URL = 'https://docs.provisionapp.io/introduction';

export const DeveloperTab = () => {
  const { apiKeys, isLoading } = usePublicApiKeys({ includeDisabled: true });
  const { rotatePublicApiKey } = useRotatePublicApiKey();
  const { deletePublicApiKey } = useDeletePublicApiKey();
  const { confirm } = useConfirmationModal();

  const [showPublicApiKeyModal] = useShowModals(Modals.PublicApiKey);

  const hasKeys = apiKeys.length > 0;

  const openCreate = () => showPublicApiKeyModal({ mode: 'create' });

  const openEditScopes = (apiKey: PublicApiKeyItem) =>
    showPublicApiKeyModal({ mode: 'edit', apiKey });

  const handleRegenerate = async (apiKey: PublicApiKeyItem) => {
    const confirmed = await confirm({
      title: 'Regenerate API key?',
      body: 'The current secret stops working right away, and a new one replaces it.',
      confirmLabel: 'Regenerate key',
      danger: true,
    });
    if (!confirmed) return;
    await rotatePublicApiKey(apiKey.apiKeyId);
  };

  const handleRevoke = async (apiKey: PublicApiKeyItem) => {
    const confirmed = await confirm({
      title: 'Revoke API key?',
      body: `${apiKey.name} stops working right away, and its record stays for audit.`,
      confirmLabel: 'Revoke key',
      danger: true,
    });
    if (!confirmed) return;
    await deletePublicApiKey(apiKey.apiKeyId);
  };

  return (
    <DeveloperTabRoot>
      <DeveloperHeader>
        <DeveloperHeaderText>
          <Typography variant="h6">API keys</Typography>
          <Typography variant="body2" color="text.secondary">
            Keys are scoped to your partner account, not individual users. Copy a key from the
            list when you need to use it.
          </Typography>
          <DeveloperDocsLink
            href={PUBLIC_API_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            View public API docs
            <DynamicIcon name="arrow-up-right" library="lucide" size={16} aria-hidden="true" />
          </DeveloperDocsLink>
        </DeveloperHeaderText>
        {hasKeys ? (
          <Button variant="contained" onClick={openCreate} data-testid="api-keys-create">
            Create API key
          </Button>
        ) : null}
      </DeveloperHeader>
      <DeveloperBody>
        {!hasKeys && !isLoading ? (
          <ApiKeysEmptyState onCreate={openCreate} />
        ) : (
          <ApiKeysTable
            apiKeys={apiKeys}
            isLoading={isLoading}
            onEditScopes={openEditScopes}
            onRegenerate={handleRegenerate}
            onRevoke={handleRevoke}
          />
        )}
      </DeveloperBody>
    </DeveloperTabRoot>
  );
};
